/**
 * GTS (Generation to Short) 관련 타입 정의
 */

// === Enum 대체 상수 ===

export const GtsAnalysisMode = {
  PRODUCT: 0,
  STORE: 1,
  CUSTOM_CONTENT: 2,
} as const;

export type GtsAnalysisModeType =
  (typeof GtsAnalysisMode)[keyof typeof GtsAnalysisMode];

export const GtsBaseType = {
  CONCEPT: 0,
  SCENE: 1,
} as const;

export type GtsBaseTypeValue = (typeof GtsBaseType)[keyof typeof GtsBaseType];

export const GtsHookType = {
  NONE: 0,
  PROBLEM: 1,
  POSITIVE: 2,
} as const;

export type GtsHookTypeValue = (typeof GtsHookType)[keyof typeof GtsHookType];

export const GtsSeason = {
  SPRING: 0,
  SUMMER: 1,
  FALL: 2,
  WINTER: 3,
} as const;

export type GtsSeasonValue = (typeof GtsSeason)[keyof typeof GtsSeason];

export const GtsCharacterType = {
  AI_RECOMMEND: 0,
  PERSON: 1,
  DOG: 2,
  CAT: 3,
  PENGUIN: 4,
  MINIATURE: 5,
  HISTORICAL: 6,
  CARTOON: 7,
  PRODUCT: 8,
  DANCING: 9,
} as const;

export type GtsCharacterTypeValue =
  (typeof GtsCharacterType)[keyof typeof GtsCharacterType];

export const GtsTempo = {
  FAST: 0,
  MEDIUM: 1,
  SLOW: 2,
} as const;

export type GtsTempoValue = (typeof GtsTempo)[keyof typeof GtsTempo];

export const GtsStep = {
  PRODUCT_INPUT: 10,
  ANALYSIS_RESULT: 20,
  CONCEPT_SELECT: 30,
  VIDEO_CREATE: 40,
} as const;

export type GtsStepValue = (typeof GtsStep)[keyof typeof GtsStep];

export const GtsProductSource = {
  CUSTOM: 1,
  COUPANG: 2,
  STORE: 3,
  GS_SHOP: 4,
} as const;

export type GtsProductSourceValue =
  (typeof GtsProductSource)[keyof typeof GtsProductSource];

export const GtsConceptMode = {
  SHOWCASE: 'showcase',
  CHARACTER: 'character',
  AI_AGENT: 'ai_agent',
} as const;

export type GtsConceptModeValue =
  (typeof GtsConceptMode)[keyof typeof GtsConceptMode];

export const GtsNumberType = {
  DEAL: 'deal',
  PRODUCT: 'product',
  PROMOTION: 'promotion',
} as const;

export type GtsNumberTypeValue =
  (typeof GtsNumberType)[keyof typeof GtsNumberType];

export const GtsContentCategory = {
  SAFETY: 'safety',
  CULTURE: 'culture',
  SALES: 'sales',
  PRODUCT_AD: 'product_ad',
  OTHER: 'other',
} as const;

export type GtsContentCategoryValue =
  (typeof GtsContentCategory)[keyof typeof GtsContentCategory];

/**
 * 제품 카테고리 (GtsProductCategory)
 * BE: com.crabs.domain.gts.code.GtsProductCategory
 * - 제품 분석 시 자동 감지되며, 사용자가 수동 변경 가능
 * - 카테고리별로 다른 무브먼트 프롬프트 적용
 */
export const GtsProductCategory = {
  FASHION: 0,
  BEAUTY: 1,
  FOOD: 2,
  HEALTH: 3,
  ELECTRONICS: 4,
  LIFESTYLE: 5,
  GENERAL: 6,
} as const;

export type GtsProductCategoryValue =
  (typeof GtsProductCategory)[keyof typeof GtsProductCategory];

export const GtsProductCategoryLabels: Record<GtsProductCategoryValue, string> =
  {
    [GtsProductCategory.FASHION]: '패션',
    [GtsProductCategory.BEAUTY]: '뷰티',
    [GtsProductCategory.FOOD]: '푸드',
    [GtsProductCategory.HEALTH]: '건강',
    [GtsProductCategory.ELECTRONICS]: '전자제품',
    [GtsProductCategory.LIFESTYLE]: '라이프스타일',
    [GtsProductCategory.GENERAL]: '일반',
  };

/**
 * SHOWCASE 모드 무브먼트 스타일 (GtsMovementStyle)
 * BE: com.crabs.domain.gts.code.GtsMovementStyle
 */
export const GtsMovementStyle = {
  NATURAL: 0,
  ENERGETIC: 1,
  ELEGANT: 2,
  PLAYFUL: 3,
  DRAMATIC: 4,
} as const;

export type GtsMovementStyleValue =
  (typeof GtsMovementStyle)[keyof typeof GtsMovementStyle];

export const GtsMovementStyleLabels: Record<GtsMovementStyleValue, string> = {
  [GtsMovementStyle.NATURAL]: '자연스러운',
  [GtsMovementStyle.ENERGETIC]: '에너제틱',
  [GtsMovementStyle.ELEGANT]: '엘레강스',
  [GtsMovementStyle.PLAYFUL]: '플레이풀',
  [GtsMovementStyle.DRAMATIC]: '드라마틱',
};

/**
 * GTS LLM 모델 (Text Generation)
 * BE: com.crabs.domain.gts.code.GtsLlmModel
 * - 영상 프롬프트, 컨셉 생성, 분석 등에 사용
 */
export const GtsLlmModel = {
  // 속성명과 숫자 코드는 저장된 프로젝트 호환용이며 labels가 현재 실행 모델을 표시한다.
  // OpenAI (1000~1999)
  GPT_5_MINI: 1000,
  GPT_5_NANO: 1001,
  GPT_5_1: 1002,
  GPT_O4_MINI: 1003,
  GPT_O3_MINI: 1004,
  GPT_O3: 1005,
  // Gemini (2000~2999)
  GEMINI_2_5_FLASH: 2000,
  GEMINI_2_5_PRO: 2001,
  GEMINI_2_0_FLASH: 2002,
  GEMINI_3_FLASH_PREVIEW: 2003,
  GEMINI_3_PRO_PREVIEW: 2004,
  // Claude (3000~3999)
  CLAUDE_4_5_SONNET: 3000,
  CLAUDE_4_5_HAIKU: 3001,
  CLAUDE_4_5_OPUS: 3002,
} as const;

export type GtsLlmModelValue = (typeof GtsLlmModel)[keyof typeof GtsLlmModel];

export const GtsLlmModelLabels: Record<GtsLlmModelValue, string> = {
  [GtsLlmModel.GPT_5_MINI]: 'GPT-5.6 Luna',
  [GtsLlmModel.GPT_5_NANO]: 'GPT-5 Nano',
  [GtsLlmModel.GPT_5_1]: 'GPT-5.4',
  [GtsLlmModel.GPT_O4_MINI]: 'GPT-5.6 Luna',
  [GtsLlmModel.GPT_O3_MINI]: 'GPT-5.6 Luna',
  [GtsLlmModel.GPT_O3]: 'GPT-5.5',
  [GtsLlmModel.GEMINI_2_5_FLASH]: 'Gemini 3.6 Flash',
  [GtsLlmModel.GEMINI_2_5_PRO]: 'Gemini 3.6 Flash',
  [GtsLlmModel.GEMINI_2_0_FLASH]: 'Gemini 3.6 Flash',
  [GtsLlmModel.GEMINI_3_FLASH_PREVIEW]: 'Gemini 3 Flash Preview',
  [GtsLlmModel.GEMINI_3_PRO_PREVIEW]: 'Gemini 3.1 Pro Preview',
  [GtsLlmModel.CLAUDE_4_5_SONNET]: 'Claude 4.6 Sonnet',
  [GtsLlmModel.CLAUDE_4_5_HAIKU]: 'Claude 4.5 Haiku',
  [GtsLlmModel.CLAUDE_4_5_OPUS]: 'Claude 4.8 Opus',
};

/**
 * GTS 이미지 생성 모델
 * BE: com.crabs.domain.gts.code.GtsImageModel
 */
export const GtsImageModel = {
  // Gemini (2000~2999)
  GEMINI_2_5_FLASH_IMAGE: 2000,
  GEMINI_2_5_PRO_IMAGE: 2001,
  GEMINI_3_PRO_IMAGE_PREVIEW: 2002,
} as const;

export type GtsImageModelValue =
  (typeof GtsImageModel)[keyof typeof GtsImageModel];

export const GtsImageModelLabels: Record<GtsImageModelValue, string> = {
  [GtsImageModel.GEMINI_2_5_FLASH_IMAGE]: 'Gemini 2.5 Flash Image',
  [GtsImageModel.GEMINI_2_5_PRO_IMAGE]: 'Gemini 3 Pro Image',
  [GtsImageModel.GEMINI_3_PRO_IMAGE_PREVIEW]: 'Gemini 3 Pro Image Preview',
};

// === 영상 생성 관련 Enum (BE와 동일한 int 코드) ===

/**
 * GTS 영상 생성 모델 (GtsVideoModel)
 * BE: com.crabs.domain.gts.code.GtsVideoModel
 */
export const GtsVideoModel = {
  // 로컬 처리 (0~999)
  FFMPEG: 0,
  // OpenAI - Sora 직접 호출 (1000~1099)
  SORA: 1000,
  SORA_PRO: 1001,
  // Atlas Cloud 경유 Sora (1100~1199) — OpenAI 직접 호출 장애 시 우회 경로
  SORA_ATLAS: 1100,
  SORA_PRO_ATLAS: 1101,
  // Google - Veo (2000~2999)
  VEO_2: 2000,
  VEO_3: 2001,
  VEO_3_FAST: 2002,
  VEO_2_0_GENERATE_001: 2003,
  VEO_3_0_GENERATE_001: 2004,
  VEO_3_1_GENERATE_PREVIEW: 2005,
  // Kling (4000~4999)
  KLING_V2: 4000,
  KLING_TURBO: 4001,
  KLING_O3_STD: 4002,
  KLING_O3_PRO: 4003,
  // Midjourney (5000~5999)
  MIDJOURNEY_VIDEO: 5000,
  MIDJOURNEY_V6: 5001,
  // Atlas Cloud - Seedance / Van / Sedeo (6000~6999)
  SEEDANCE_1_5_PRO: 6000,
  VAN_2_6: 6100,
  SEDEO_2_0: 6200,
  SEDEO_2_0_FAST: 6201,
  // xAI - Grok (7000~7999)
  GROK: 7000,
  // BytePlus (8000~8999)
  OMNIHUMAN_1_5: 8001,
  SEEDANCE_2_0: 8002,
  SEEDANCE_2_5: 8003,
} as const;

export type GtsVideoModelValue =
  (typeof GtsVideoModel)[keyof typeof GtsVideoModel];

export const GtsVideoModelLabels: Record<GtsVideoModelValue, string> = {
  [GtsVideoModel.FFMPEG]: 'FFMPEG (Local)',
  [GtsVideoModel.SORA]: 'Sora 2 (OpenAI)',
  [GtsVideoModel.SORA_PRO]: 'Sora 2 Pro (OpenAI)',
  [GtsVideoModel.SORA_ATLAS]: 'Sora 2 (Atlas)',
  [GtsVideoModel.SORA_PRO_ATLAS]: 'Sora 2 Pro (Atlas)',
  [GtsVideoModel.VEO_2]: 'Veo 2 (Google)',
  [GtsVideoModel.VEO_3]: 'Veo 3 (Google)',
  [GtsVideoModel.VEO_3_FAST]: 'Veo 3 Fast (Google)',
  [GtsVideoModel.VEO_2_0_GENERATE_001]: 'Veo 2.0 Generate 001 (Google)',
  [GtsVideoModel.VEO_3_0_GENERATE_001]: 'Veo 3.0 Generate 001 (Google)',
  [GtsVideoModel.VEO_3_1_GENERATE_PREVIEW]: 'Veo 3.1 Generate Preview (Google)',
  [GtsVideoModel.KLING_V2]: 'Kling V2 (Atlas)',
  [GtsVideoModel.KLING_TURBO]: 'Kling Turbo (Atlas)',
  [GtsVideoModel.KLING_O3_STD]: 'Kling O3 Std (Atlas)',
  [GtsVideoModel.KLING_O3_PRO]: 'Kling O3 Pro (Atlas)',
  [GtsVideoModel.MIDJOURNEY_VIDEO]: 'Midjourney Video (Midjourney)',
  [GtsVideoModel.MIDJOURNEY_V6]: 'Midjourney V6 (Midjourney)',
  [GtsVideoModel.VAN_2_6]: 'Van 2.6 (Atlas)',
  [GtsVideoModel.SEEDANCE_1_5_PRO]: 'Seedance 1.5 Pro (Atlas)',
  [GtsVideoModel.SEDEO_2_0]: 'Seedance 2.0 (Atlas)',
  [GtsVideoModel.SEDEO_2_0_FAST]: 'Seedance 2.0 Fast (Atlas)',
  [GtsVideoModel.GROK]: 'Grok Video (xAI)',
  [GtsVideoModel.OMNIHUMAN_1_5]: 'OmniHuman 1.5 (BytePlus)',
  [GtsVideoModel.SEEDANCE_2_0]: 'Seedance 2.0 (BytePlus)',
  [GtsVideoModel.SEEDANCE_2_5]: 'Seedance 2.5 (BytePlus)',
};

/**
 * GtsVideoModel → BE VendorModel code 매핑.
 * AI-API의 VendorModel enum value와 일치해야 함.
 * GTS_CHAT 채팅에서 영상 생성 모델을 AI-API로 전달할 때 사용.
 */
export const GtsVideoModelToVendorCode: Partial<
  Record<GtsVideoModelValue, string>
> = {
  [GtsVideoModel.SORA]: 'sora-2',
  [GtsVideoModel.SORA_PRO]: 'sora-2-pro',
  [GtsVideoModel.SORA_ATLAS]: 'sora-2-atlas',
  [GtsVideoModel.SORA_PRO_ATLAS]: 'sora-2-pro-atlas',
  [GtsVideoModel.VEO_2]: 'veo-2',
  [GtsVideoModel.VEO_3]: 'veo-3',
  [GtsVideoModel.VEO_3_0_GENERATE_001]: 'veo-3.0-generate-001',
  [GtsVideoModel.VEO_3_1_GENERATE_PREVIEW]: 'veo-3.1-generate-preview',
  [GtsVideoModel.KLING_V2]: 'kling-v2',
  [GtsVideoModel.MIDJOURNEY_V6]: 'midjourney-v6',
  [GtsVideoModel.SEEDANCE_1_5_PRO]: 'seedance-1.5-pro',
  [GtsVideoModel.SEDEO_2_0]: 'atlascloud/sedeo-2.0',
  [GtsVideoModel.SEDEO_2_0_FAST]: 'atlascloud/sedeo-2.0-fast',
  [GtsVideoModel.OMNIHUMAN_1_5]: 'omnihuman-1.5',
  [GtsVideoModel.SEEDANCE_2_0]: 'dreamina-seedance-2-0-260128',
  [GtsVideoModel.SEEDANCE_2_5]: 'dreamina-seedance-2-5-260628',
};

/**
 * @deprecated Use GtsVideoModel instead
 */
export const VideoProvider = GtsVideoModel;
export type VideoProviderValue = GtsVideoModelValue;

/**
 * 해상도 (Resolution)
 * BE: com.crabs.domain.project.code.Resolution
 */
export const Resolution = {
  AUTO: 0,
  SD: 1, // 480p
  HD: 2, // 720p
  FHD: 3, // 1080p
  QHD: 4, // 2k
  UHD: 5, // 4k
} as const;

export type ResolutionValue = (typeof Resolution)[keyof typeof Resolution];

/**
 * 화면 비율 (AspectRatio)
 * BE: com.crabs.domain.project.code.AspectRatio
 */
export const AspectRatio = {
  NINE_TO_SIXTEEN: 1, // 9:16 (세로)
  ONE_TO_ONE: 2, // 1:1
  SIXTEEN_TO_NINE: 3, // 16:9 (가로)
} as const;

export type AspectRatioValue = (typeof AspectRatio)[keyof typeof AspectRatio];

/**
 * 영상 품질 (VideoQuality)
 * BE: com.crabs.domain.gts.code.VideoQuality
 */
export const VideoQuality = {
  STANDARD: 0,
  HIGH: 1,
} as const;

export type VideoQualityValue =
  (typeof VideoQuality)[keyof typeof VideoQuality];

/**
 * 영상 생성 상태 (GtsVideoStatus)
 * BE: com.crabs.domain.gts.code.GtsVideoStatus
 */
export const GtsVideoStatusCode = {
  PROCESSING: 0,
  COMPLETED: 1,
  FAILED: 2,
} as const;

export type GtsVideoStatusCodeValue =
  (typeof GtsVideoStatusCode)[keyof typeof GtsVideoStatusCode];

/**
 * 이미지 생성 모드 (GtsImageMode)
 * BE: com.crabs.domain.gts.code.GtsImageMode
 */
export const GtsImageMode = {
  NORMAL: 0,
  STORYBOARD: 1,
} as const;

export type GtsImageModeValue =
  (typeof GtsImageMode)[keyof typeof GtsImageMode];

/**
 * 영상 다듬기 모드 (GtsVideoExtendMode)
 * BE: com.crabs.domain.gts.code.GtsVideoExtendMode
 */
export const GtsVideoExtendMode = {
  PRESERVE: 0, // 보존형
  KDUB: 1, // 더빙형
} as const;

export type GtsVideoExtendModeValue =
  (typeof GtsVideoExtendMode)[keyof typeof GtsVideoExtendMode];

/**
 * 콘텐츠 소스 (ContentSource)
 * BE: com.crabs.domain.gts.code.ContentSource
 */
export const ContentSource = {
  AI_GENERATED: 1,
  UPLOAD: 2,
  CRAWLED: 3,
} as const;

export type ContentSourceValue =
  (typeof ContentSource)[keyof typeof ContentSource];

/**
 * 파일 확장자 (FileExtension)
 * BE: com.crabs.domain.content.code.FileExtension
 */
export const FileExtension = {
  // 비디오
  MP4: 0,
  MOV: 1,
  AVI: 2,
  MKV: 3,
  // 오디오
  MP3: 10,
  WAV: 11,
  AAC: 12,
  // 이미지
  JPEG: 20,
  PNG: 21,
  JPG: 22,
  GIF: 23,
  // 폰트
  TTF: 30,
  OTF: 31,
  WOFF: 32,
  WOFF2: 33,
  // 기타
  TS: 100,
} as const;

export type FileExtensionValue =
  (typeof FileExtension)[keyof typeof FileExtension];

// === 인터페이스 ===

/**
 * 영상 생성 모델 설정
 * BE: GtsVideoModelSettings (int 코드로 전송)
 */
export interface GtsModelSettings {
  videoProvider: GtsVideoModelValue;
  resolution: ResolutionValue;
  aspectRatio: AspectRatioValue;
  quality: VideoQualityValue;
  length: number;
  useSubtitles?: boolean;
  useVoice?: boolean; // 영상 내 자동 음성 사용 여부 (기본값: true)
  useProductNameCall?: boolean; // 영상 내 제품명 호명 여부 (기본값: true)
}

export interface TtsSettings {
  voice: string;
  speedFactor: number;
  pitch: number;
  emotion: string;
}

// === 컨텐츠 데이터 관련 타입 ===

export type GtsContentSource = 'upload' | 'crawl' | 'crop' | 'ai_generated';

export interface GtsContentItem {
  s3Key?: string | null;
  s3Bucket?: string | null;
  publicUrl: string;
  extension?: string | null; // 파일 확장자
  originalUrl?: string | null; // 크롤링 이미지의 경우 원본 URL
  source: GtsContentSource;
}

/**
 * 이미지 역할 선택 정보
 * 크롤링/업로드 이미지 풀에서 선택한 이미지의 역할을 지정
 */
export interface GtsSelectedImage {
  publicUrl: string;
  source: 'crawled' | 'uploaded';
  originalTab?: 'main' | 'detail' | 'review' | 'uploaded';
  s3Key?: string | null;
}

export interface GtsSelectedCrawlImage extends GtsContentItem {
  crawlType: 'mainImage' | 'detailImage' | 'reviewImage';
}

export interface GtsGeneratedVideo extends GtsContentItem {
  conceptId?: number | null;
}

/**
 * 제품 발라내기 결과
 * CHARACTER 모드에서 이미지에서 제품만 추출한 결과
 */
export interface ExtractedProduct {
  id: string;
  imageUrl: string;
  sourceImageUrl: string; // 원본 이미지 URL (연결용)
  contentId: number; // BE Content 테이블 ID
  createdAt: number;
}

/**
 * GTS 상태 DTO
 * [v2.0] 컨텐츠는 /v2.0/project/{projectId}/gts/contents API로 별도 조회
 * - 크롤링 이미지: ProjectContentType.MAIN/DETAIL/REVIEW
 * - 업로드 이미지: ProjectContentType.BASE
 * - 씬 이미지/비디오/오디오: ProjectContentType.SCENE + referenceId
 */
export interface GtsState {
  id: number;
  projectId: number;
  maxStep: GtsStepValue;
  analysisMode: GtsAnalysisModeType;
  productName?: string | null;
  productUrl?: string | null;
  storeName?: string | null;
  storeAddress?: string | null;
  userPreferences?: string | null;
  customContentCategory?: string | null;
  customContentText?: string | null;
  productAnalysis?: string | null;
  selectedCharacterType: GtsCharacterTypeValue;
  selectedConceptId?: number | null;
  customConceptInputs?: string | null;
  hookType: GtsHookTypeValue;
  enhanceSafety: boolean;
  seasonEnabled: boolean;
  selectedSeason?: GtsSeasonValue | null;
  videoCreationMode: GtsBaseTypeValue;
  modelSettings: GtsModelSettings;
  selectedBrandId?: number | null;
  displayProductName?: string | null;
  storyboardScenes?: string | null;
  narrationScript?: string | null;
  // 제품 카테고리 (AI 분석 시 자동 감지, 사용자 수동 변경 가능)
  productCategory?: GtsProductCategoryValue | null;
  // 크롤링 이미지 crop 작업 완료 여부 (polling 종료 조건)
  cropFinished?: boolean;
  // 기획전(프로모션) 상품 목록 (null이면 일반 상품)
  promotionProducts?: PromotionProduct[] | null;
  // 상품 소스 (0: GS Shop, 1: 쿠팡, 2: 직접 입력)
  productSource?: GtsProductSourceValue | null;
  // 컨셉 생성 상태 (null: 미생성, 0: 진행중, 1: 완료, 2: 실패)
  conceptGenerationStatus?: number | null;
  // 컨셉 생성 실패 시 에러 메시지
  conceptGenerationError?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GtsConcept {
  id: number;
  projectId: number;
  characterType: GtsCharacterTypeValue;
  tempo: GtsTempoValue;
  name: string;
  keywords?: string | null;
  directingPoints?: string | null;
  cutCount?: string | null;
  videoType?: string | null;
  rationale?: string | null;
  isCustom?: boolean | null;
  isBrandSpecific?: boolean | null;
  recommended?: boolean | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * 서버에서 반환하는 씬 DTO
 */
export interface GtsSceneFromServer {
  id: number;
  projectId: number;
  conceptId?: number | null;
  sceneNumber: number;
  description: string;
  narration?: string | null;
  generatedImageUrl?: string | null;
  generatedVideoUrl?: string | null;
  generatedAudioUrl?: string | null;
  imageGenerating?: boolean | null;
  videoGenerating?: boolean | null;
  audioGenerating?: boolean | null;
  videoOperationId?: string | null;
  ttsVoice?: string | null;
  ttsSpeedFactor?: number | null;
  ttsPitch?: number | null;
  ttsEmotion?: string | null;
  productImageUrl?: string | null;
  modelPhotoUrl?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GtsScene {
  id: number;
  projectId: number;
  sceneNumber: number;
  description?: string | null;
  narration?: string | null;
  ttsVoice?: string | null;
  ttsSpeedFactor?: number | null;
  ttsPitch?: number | null;
  ttsEmotion?: string | null;
  imageGenerating?: boolean;
  videoGenerating?: boolean;
  audioGenerating?: boolean;
  productImageUrl?: string | null;
  modelPhotoUrl?: string | null;
  createdAt: string;
  updatedAt: string;

  // 서버에서 직접 받은 URL 필드
  imageUrl?: string;
  videoUrl?: string;
  audioUrl?: string;
  uploadedImageBase64?: string;
  selectedModelIds?: string[];
}

/**
 * 서버 응답을 프론트엔드 씬 타입으로 변환
 */
export function convertServerSceneToScene(
  serverScene: GtsSceneFromServer,
): GtsScene {
  return {
    id: serverScene.id,
    projectId: serverScene.projectId,
    sceneNumber: serverScene.sceneNumber,
    description: serverScene.description,
    narration: serverScene.narration,
    ttsVoice: serverScene.ttsVoice,
    ttsSpeedFactor: serverScene.ttsSpeedFactor,
    ttsPitch: serverScene.ttsPitch,
    ttsEmotion: serverScene.ttsEmotion,
    imageGenerating: serverScene.imageGenerating ?? false,
    videoGenerating: serverScene.videoGenerating ?? false,
    audioGenerating: serverScene.audioGenerating ?? false,
    productImageUrl: serverScene.productImageUrl,
    modelPhotoUrl: serverScene.modelPhotoUrl,
    createdAt: serverScene.createdAt,
    updatedAt: serverScene.updatedAt,
    imageUrl: serverScene.generatedImageUrl ?? undefined,
    videoUrl: serverScene.generatedVideoUrl ?? undefined,
    audioUrl: serverScene.generatedAudioUrl ?? undefined,
  };
}

export interface GtsBgmOption {
  id: number;
  name: string;
  genre?: string | null;
  url: string;
}

export interface Brand {
  id: number;
  spaceId: number;
  name: string;
  logoUrl: string;
  logoTextKr?: string | null;
  logoTextEn?: string | null;
  isActive?: boolean | null;
  createdAt: string;
  updatedAt: string;
}

export interface BrandCreateRequest {
  name: string;
  logoUrl: string;
  logoTextKr?: string | null;
  logoTextEn?: string | null;
}

/**
 * 브랜드 로고 업로드 요청
 * extension은 @/shared/constants/dto의 DtoExtension 값 사용
 */
export interface BrandLogoUploadRequest {
  extension: number; // DtoExtension 코드 값 (jpeg: 20, png: 21, jpg: 22, gif: 23)
}

export interface BrandLogoUploadResponse {
  presignedUrl: string;
  s3Key: string;
  logoUrl: string;
}

export interface GtsSystemSetting {
  id: number;
  spaceId: number;
  defaultModelSettings: GtsModelSettings;
  defaultTtsSettings: TtsSettings;
  defaultCharacterType: GtsCharacterTypeValue;
  defaultHookType: GtsHookTypeValue;
  defaultEnhanceSafety: boolean;
  createdAt: string;
  updatedAt: string;
}

// === API 요청/응답 타입 ===

export interface GtsAnalyzeRequest {
  analysisMode: GtsAnalysisModeType;
  productUrl?: string | null;
  promotionProductUrls?: string[] | null;
  storeName?: string | null;
  storeAddress?: string | null;
  storeUrl?: string | null;
  userPreferences?: string | null;
  customContentCategory?: string | null;
  customContentText?: string | null;
  isReanalysis?: boolean;
}

export interface GtsAnalyzeResponse {
  productAnalysis: string;
  productName?: string | null;
  extractedKeywords?: string[] | null;
}

export interface GtsConceptGenerateRequest {
  characterType: GtsCharacterTypeValue;
  videoLength?: number;
  hookType?: GtsHookTypeValue;
  enhanceSafety?: boolean;
  seasonEnabled?: boolean;
  season?: GtsSeasonValue | null;
}

export interface GtsConceptGenerateResponse {
  concepts: GtsConcept[];
}

/**
 * GTS 영상 생성 모드
 * 갤러리에서 모드별 카테고라이징에 사용
 */
export type GtsGenerationMode = 'showcase' | 'concept' | 'scene' | 'chat';

export const GtsGenerationModeValue = {
  SHOWCASE: 0,
  CONCEPT: 1,
  SCENE: 2,
  CHAT: 3,
} as const;

export interface GtsVideoGenerateRequest {
  conceptId?: number | null;
  sceneId?: number | null; // 씬 모드일 때 씬 ID
  referenceImageUrl?: string | null;
  referenceImageUrls?: string[] | null; // R2V 복수 참조 이미지 (딜/기획전, 최대 9장). 단수는 하위호환 유지
  videoProvider: GtsVideoModelValue; // GtsVideoModel int 코드
  modelSettings: GtsModelSettings;
  displayProductName?: string | null; // 호명될 제품명 (useProductNameCall=true일 때 사용)
  mode: number; // GtsGenerationMode 코드 (0: showcase, 1: concept, 2: scene, 3: chat)
  // 직접 프롬프트 입력 (있으면 concept/product 대신 이 프롬프트를 사용)
  prompt?: string | null;
  // SHOWCASE 모드 전용
  movementStyle?: GtsMovementStyleValue | null; // GtsMovementStyle 코드
  productCategory?: GtsProductCategoryValue | null; // GtsProductCategory 코드
  // CONCEPT 모드 전용
  videoPromptModel?: GtsLlmModelValue | null; // GtsLlmModel 호환 코드 (기본 실행: Gemini 3.6 Flash)
  // 추가 지시사항
  customPrompt?: string | null;
  // 영상 생성 클릭 시점 사전 처리(prepare-references)로 제품추출(누끼)된 이미지의 1-based 인덱스.
  // Seedance 프롬프트에서 해당 이미지를 "서브"로 강제하는 데 쓴다. 없으면 미전달(정상).
  extractedIndices?: number[] | null;
  // 이미지별 상품명(referenceImageUrls 와 정렬). prepare-references 응답값을 그대로 전달한다.
  // byteplus 400 self-heal 재추출 시 각 이미지를 자기 상품명으로 정확히 누끼하는 데 쓴다.
  referenceProductNames?: string[] | null;
}

export interface GtsVideoGenerateResponse {
  status: number; // GtsVideoStatus 코드 (0: processing, 1: completed, 2: failed)
  videoUrl: string | null;
  contentId: number | null; // content 테이블에 저장하지 않으므로 null
  s3Key: string | null;
  s3PublicUrl: string | null;
  videoId: string | null;
}

export interface GtsVideoStatus {
  status: number; // GtsVideoStatus 코드 (0: processing, 1: completed, 2: failed)
  videoUrl: string | null;
  contentId: number | null;
  s3Key: string | null;
  s3PublicUrl: string | null;
  videoId?: string | null;
}

// GTS 갤러리 비디오 아이템 (BE GtsGalleryVideoItemDto 매칭)
export interface GtsGalleryVideoItem {
  id: string;
  s3Key: string;
  s3Bucket?: string;
  publicUrl: string;
  extension?: FileExtensionValue | null; // FileExtension 코드 (0: MP4, 1: MOV, etc.)
  thumbnailS3Key?: string | null; // 썸네일 S3 키
  thumbnailUrl?: string | null; // 썸네일 URL
  thumbnailExtension?: FileExtensionValue | null; // FileExtension 코드 (20: JPEG, 21: PNG, etc.)
  mode: number; // GtsGenerationMode 코드 (0: showcase, 1: concept, 2: scene, 3: chat)
  status: number; // GtsVideoStatus 코드 (0: processing, 1: completed, 2: failed)
  conceptId?: number | null;
  sceneId?: number | null;
  sceneNumber?: number | null; // 씬 번호 (파일명 생성용)
  createdAt: string;
  updatedAt?: string;
  savedToAsset: boolean;
  contentId?: number | null; // Content 테이블에 저장된 경우의 ID
  width?: number | null;
  height?: number | null;
  duration?: number | null; // ms
  fileSize?: number | null;
  narration?: string | null; // 대본 다운로드용 나레이션 (컨셉의 directingPoints에서 자동 저장)
  videoProvider?: number | null; // GtsVideoModel enum 코드 (1000: SORA, 2000: VEO_2, 4000: KLING_V2 등)
  videoErrorMessage?: string | null; // AI-API에서 분류된 에러 메시지 (토스트용)
  parentId?: number | null; // multi-scene 그룹핑용 부모 Content ID
  sceneIndex?: number | null; // multi-scene scene 순서 (0-based)
  generationPrompt?: string | null; // 영상 생성에 사용된 프롬프트
  progress?: number | null; // 영상 생성 진행률 (0-100, PROCESSING 상태에서만)
  metadata?: {
    provider?: number; // GtsVideoModel 코드
    status?: number; // GtsVideoStatus 코드
    duration?: number;
    width?: number;
    height?: number;
    error?: string;
  };
}

export interface GtsImageGenerateRequest {
  conceptId?: number | null;
  productImageContentId?: number | null; // 제품 이미지 contentId (be-api에서 URL 조회)
  brandLogoUrl?: string | null;
  includeBrandLogo?: boolean;
  mode?: GtsImageModeValue; // GtsImageMode 코드 (0: NORMAL, 1: STORYBOARD)
  storyboardScenes?: { description: string }[] | null;
}

export interface GtsImageGenerateResponse {
  s3Key?: string;
  s3PublicUrl?: string;
}

export interface GtsTtsRequest {
  text: string;
  voice?: string;
  speedFactor?: number;
  pitch?: number;
  emotion?: string;
}

export interface GtsTtsResponse {
  contentId?: number;
  s3Key: string;
  s3PublicUrl: string;
}

export interface GtsSceneGenerateRequest {
  conceptId: number;
  conceptInfo: {
    name: string;
    keywords?: string | null;
    directingPoints?: string | null;
    cutCount?: string | null;
    tempo: GtsTempoValue;
    videoType?: string | null;
    rationale?: string | null;
  };
  productAnalysis: string;
  sceneCount?: number;
  videoLength?: number;
}

export interface GtsGeneratedScene {
  id: number; // 서버에서 생성된 씬 ID
  sceneNumber: number;
  description: string;
  narration?: string | null;
}

export interface GtsSceneGenerateResponse {
  scenes: GtsGeneratedScene[];
}

// === 앱 상태 (Frontend 전용) ===

export interface GtsAppState {
  // 현재 단계
  currentStep: GtsStepValue;

  // 분석 관련
  productSource: GtsProductSourceValue;
  analysisMode: GtsAnalysisModeType;
  numberType: GtsNumberTypeValue;
  productUrl: string;
  storeName: string;
  storeAddress: string;
  contentCategory: GtsContentCategoryValue;
  customContentCategory: string;
  customContentText: string;
  userPreferences: string;

  // 분석 결과
  productAnalysis: string;
  productName: string;
  displayProductName: string; // 영상에서 호명될 제품명 (사용자 수정 가능)
  productImageBase64: string;
  productImageContentId: number | null;

  // 컨셉 관련
  concepts: GtsConcept[];
  selectedConceptIndex: number | null;
  selectedConceptId: number | null;

  // 씬 관련
  scenes: GtsScene[];

  // 참조 이미지
  referenceImage: string;
  modelPhotos: string[];

  // 설정
  modelSettings: GtsModelSettings;
  selectedCharacterType: GtsCharacterTypeValue;
  hookType: GtsHookTypeValue;
  seasonEnabled: boolean;
  season: GtsSeasonValue | null;
  enhanceSafety: boolean;

  // 브랜드
  selectedBrandId: number | null;
  brandName: string;
  brandLogoUrl: string;

  // 생성 상태
  isAnalyzing: boolean;
  isGeneratingConcepts: boolean;
  isGeneratingImage: boolean;
  isGeneratingVideo: boolean;
}

// === 크롤링 관련 타입 ===

/**
 * 기획전(프로모션) 상품 정보
 * BE GtsState.promotionProducts JSON의 개별 요소
 */
export interface PromotionProduct {
  prdId: string;
  name?: string | null;
  brandName?: string | null;
  price?: number | null;
  /** 할인 전 원래 가격 (딜에서 flgdPrc) */
  originalPrice?: number | null;
  thumbnail?: string | null;
  /** 선택 순서 (1, 2, 3). 미선택 시 null */
  order?: number | null;
  /** 현재 선택 여부 */
  selected: boolean;
  /** 하위 크롤링(상세/리뷰) 완료 여부 */
  crawled: boolean;
}

export interface GtsCrawledContentItem {
  seq: number;
  type: 'text' | 'image' | 'video';
  value: string;
  description?: string;
}

export interface GtsCrawledReview {
  reviewId: string;
  author: string;
  rating: number;
  text: string;
  images: string[];
  like: number;
  date?: string;
}

export interface GtsCrawledData {
  type: 'PRD' | 'DEAL' | 'PROMOTION';
  prdId?: string;
  dealNo?: string;
  planseq?: string;
  title: string;
  category: string;
  thumbnail: string;
  price?: number;
  originalPrice?: number;
  mainImages: string[];
  originalDetailImages: string[]; // deprecated: contentItems에 통합됨
  contentItems: GtsCrawledContentItem[];
  reviews: GtsCrawledReview[];
  promotionProducts?: PromotionProduct[];
  crawledAt?: string;
  cropFinished?: boolean; // crop 완료 여부 (true: 완료, false/undefined: 미완료)
}

export interface GtsCrawlRequest {
  url: string;
}

export interface GtsCrawlResponse {
  status: 'ok' | 'error';
  error?: string;
  data?: GtsCrawledData;
}
